<template>
  <div class="bernoulli-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$P + ½ρv² = Constant$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <!-- 核心文丘里管动态 SVG -->
        <svg width="100%" height="240" viewBox="0 0 500 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <linearGradient id="fluid-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#0ea5e9" stop-opacity="0.1" />
                <stop offset="50%" stop-color="#38bdf8" stop-opacity="0.3" />
                <stop offset="100%" stop-color="#0ea5e9" stop-opacity="0.1" />
             </linearGradient>
             <filter id="glow-cyan"><feGaussianBlur stdDeviation="2" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 变径管道 (文丘里管形状) -->
          <path :d="pipePath" fill="url(#fluid-grad)" stroke="#1e293b" stroke-width="2" />
          <path :d="pipePath" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="4" stroke-dasharray="10,5" />

          <!-- 流体粒子流 -->
          <circle v-for="p in particles" :key="p.id" 
                  :cx="p.x" :cy="getPipeCenter(p.x, p.offsetY)" 
                  r="2" fill="#38bdf8" :opacity="p.opacity" filter="url(#glow-cyan)" />

          <!-- 压力计组 (压强计) -->
          <g v-for="g in gauges" :key="g.x" :transform="`translate(${g.x}, ${g.y})`">
             <!-- 连通管 -->
             <line x1="0" y1="0" x2="0" y2="20" stroke="#334155" stroke-width="1.5" />
             <!-- 表盘 -->
             <circle r="18" fill="#0f172a" stroke="#475569" stroke-width="1.5" />
             <path d="M -12,0 A 12,12 0 0 1 12,0" fill="none" stroke="#1e293b" />
             <!-- 指针 -->
             <line x1="0" y1="0" :x2="14 * Math.cos(g.angle)" :y2="14 * Math.sin(g.angle)" 
                   :stroke="g.color" stroke-width="2" stroke-linecap="round" />
             <text y="35" text-anchor="middle" font-size="9" :fill="g.color" font-weight="900">P = {{ g.p.toFixed(1) }}</text>
             <text y="-22" text-anchor="middle" fill="#64748b" font-size="8" font-weight="bold">{{ g.label }}</text>
          </g>

          <text x="250" y="230" text-anchor="middle" fill="#334155" font-size="10" font-weight="bold">文丘里(Venturi)管横截面流量模拟</text>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>整体流速级</label>
              <span class="val">{{ flowSpeed }} <small>v</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">全局泵送速率 (v_pump)</label>
                 <el-slider v-model="flowSpeed" :min="1" :max="15" size="small"></el-slider>
              </div>
              <el-button type="info" size="mini" plain icon="el-icon-refresh-left" @click="resetParticles">重置流场</el-button>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理逻辑：</strong>在不可压缩流体中，<strong>截面积减小意味着流速必定增加</strong>。根据伯努利方程，流速增加处动压升高，静压（测得压力）必然降低。观察中间窄窄的部分：粒子跑得最快，但表盘显示的压强 $ P $ 却是全管最低的。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BernoulliViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      flowSpeed: 8,
      particles: [],
      nextId: 0,
      running: true
    }
  },
  computed: {
    // Correct Venturi pipe shape: wide at ends, narrow center
    pipePath() {
       return `M 0,80 L 150,80 C 220,80 220,110 250,110 C 280,110 280,80 350,80 L 500,80
               L 500,180 L 350,180 C 280,180 280,150 250,150 C 220,150 220,180 150,180 L 0,180 Z`;
    },
    gauges() {
       const vBase = this.flowSpeed;
       // P = Total - 0.5 * rho * v^2. Here we mock the values for visualization.
       return [
         { label: '入口', x: 80, y: 70, p: 100 - vBase*2, angle: this.getAngle(100 - vBase*2), color: '#38bdf8' },
         { label: '喉部(窄)', x: 250, y: 100, p: 100 - vBase*5, angle: this.getAngle(100 - vBase*5), color: '#f43f5e' },
         { label: '出口', x: 420, y: 70, p: 100 - vBase*2.2, angle: this.getAngle(100 - vBase*2.2), color: '#38bdf8' }
       ];
    }
  },
  methods: {
    getAngle(p) {
       // Map pressure to gauge angle (roughly -150 to -30 degrees)
       return (Math.max(0, p) / 100) * Math.PI - Math.PI;
    },
    getPipeCenter(x, offset) {
       // Helper to keep particles inside the widening/narrowing pipe
       let centerY = 130;
       let halfHeight = 50;
       if (x > 150 && x < 350) {
          const dist = Math.abs(x - 250);
          halfHeight = 20 + (dist / 100) * 30;
       }
       return centerY + offset * (halfHeight / 50);
    },
    resetParticles() {
       this.particles = Array.from({ length: 60 }, (_, i) => ({
          id: i,
          x: Math.random() * 500,
          offsetY: (Math.random() - 0.5) * 80,
          opacity: 0.3 + Math.random() * 0.7
       }));
    },
    animate() {
       if (!this.running) return;
       this.particles.forEach(p => {
          // Continuity equation: Area1 * v1 = Area2 * v2
          // Area is proportional to width. Width is 100 outside, 40 at center.
          // v_local = v_base * (100 / current_width)
          let currentWidth = 100;
          if (p.x > 150 && p.x < 350) {
             const dist = Math.abs(p.x - 250);
             currentWidth = 40 + (dist / 100) * 60;
          }
          const v_local = this.flowSpeed * (100 / currentWidth);
          p.x += v_local * 0.3;
          if (p.x > 500) p.x = 0;
       });
       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.resetParticles();
    this.animate();
  },
  beforeDestroy() {
    this.running = false;
    cancelAnimationFrame(this.animFrame);
  }
}
</script>

<style scoped lang="scss">
.bernoulli-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f1f5f9; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
